import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';
import getConfig from 'next/config';

import { PRODUCT_CODE_OFFSET } from 'src/constants/reviews';
import type { NextConfig } from 'src/types/nextConfig';
import { logger } from 'src/utils/serverLogger';
import type { ReviewSummaryResponse } from './types/ReviewSummaryResponse';

const { serverRuntimeConfig } = getConfig() as NextConfig;

// Reviews data is non-critical: the page renders without it, so it gets the
// tight budget, a miss logs at debug and a failure at warn, not error. See the
// DISC-4325 spec's failure and latency policy.
export const TIMEOUT = 500;

export const fetchReviewSummary = async (
  productCode: number,
): Promise<ReviewSummaryResponse | null> => {
  // Read per call, not at module scope: serverRuntimeConfig is empty in the client bundle.
  const requestUrl = `${serverRuntimeConfig.endpoints.reviewSummary}/customer/${
    productCode - PRODUCT_CODE_OFFSET
  }`;

  const response = await fetchJSONWithTimeout(requestUrl, { timeout: TIMEOUT });

  switch (response.status) {
    case 200:
      return response.json();
    // The service answers 400 when a product has too few reviews to summarize.
    // Expected for most products, so not worth a line in production.
    case 400:
      logger.debug('too few reviews for an AI summary in reviews-summary service', {
        requestUrl,
      });

      return null;
    // A 404 means the service does not know the product at all, which is rare
    // and could point at the product code offset, so it stays visible.
    case 404:
      logger.warn('no AI summary found in reviews-summary service', { requestUrl });

      return null;
    default:
      logger.warn('error fetching AI summary from reviews-summary service', {
        requestUrl,
        status: response.status,
      });

      return null;
  }
};
